import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { branding } from '../constants/branding';
import { generateKeyPair, decryptData } from '../utils/crypto';
import { setKeys, setShowSensitiveData, setTimeoutId, clearCardSecurity } from '../store/slices/cardSecuritySlice';
import { RootState } from '../store';
import { useAppSelector } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';

// Spending categories for analytics
const spendingCategories = [
  { category: 'Shopping', amount: 215.74, percentage: 45, color: '#ff6b6b' },
  { category: 'Food', amount: 17.49, percentage: 15, color: '#4ecdc4' },
  { category: 'Transport', amount: 83.50, percentage: 25, color: '#45b7d1' },
  { category: 'Entertainment', amount: 0, percentage: 0, color: '#96ceb4' },
];

const visaLogo = 'https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg';

interface CardTransaction {
  id: number;
  merchant: string;
  amount: number;
  date: string;
  category: string;
}

interface CardDetails {
  cardNumber: string;
  expiryDate: string;
  cvc: string;
}

const Cards: React.FC = () => {
  const dispatch = useDispatch();
  const [cards, setCards] = useState<any[]>([]);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [cardTypeToAdd, setCardTypeToAdd] = useState<'Virtual' | 'Physical' | null>(null);
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [frozenCards, setFrozenCards] = useState<Set<number>>(new Set());
  
  const MOBILE_BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // Get card security state from Redux
  const cardSecurity = useSelector((state: RootState) => state.cardSecurity);

  const token = useAppSelector((state) => state.auth.token);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCards = async () => {
    if (!token) {
      setError('Authentication token not available');
      setIsLoading(false);
      return;
    }

    try {
      console.log('Fetching cards...');
      setIsLoading(true);
      setError(null);
      
      const response = await axiosInstance.get('/cards');
      
      console.log('Cards response:', response);
      
      if (response.status === 200) {
        const data = response.data;
        console.log('Cards data:', data);
        
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          const cardsData = data.data || [];
          setCards(cardsData);
          setError(null);
        } else {
          console.error('Failed to fetch cards:', data.message);
          setError(data.message || 'Failed to fetch cards');
        }
      } else {
        setError(`Server error: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error fetching cards:', error);
      if (error.response?.status !== 401) { // Don't set error for 401s as they're handled by interceptor
        setError(error.message || 'An error occurred while fetching cards');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCards();
    }
  }, [token]);

  useEffect(() => {
    if (cardSecurity.showSensitiveData) {
      setCountdown(15);
      const timer = setInterval(() => {
        setCountdown(prev => prev !== null ? prev - 1 : null);
      }, 1000);
      return () => {
        clearInterval(timer);
        setCountdown(null);
      };
    }
  }, [cardSecurity.showSensitiveData]);

  const handleCardTypeSelect = (type: 'Virtual' | 'Physical') => {
    setCardTypeToAdd(type);
    setShowAddCardModal(false);
    setShowOtpModal(true);
  };

  const handleVerifyOtp = async () => {
    // Basic OTP validation
    if (otp === '123456' && cardTypeToAdd) {
      if (!token) {
        setError('Authentication token not available');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const newCard = {
          type: cardTypeToAdd,
          number: `4000${Math.floor(1000 + Math.random() * 9000)}${Math.floor(1000 + Math.random() * 9000)}${Math.floor(1000 + Math.random() * 9000)}`,
          expiryDate: `${Math.floor(1 + Math.random() * 12).toString().padStart(2, '0')}/${new Date().getFullYear() % 100 + 4}`,
          cvc: Math.floor(100 + Math.random() * 900).toString(),
          holder: "John Doe",
          balance: 0.00,
          limit: cardTypeToAdd === 'Physical' ? 5000.00 : 2500.00,
          status: "Active",
          color: cardTypeToAdd === 'Physical' 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        };

        console.log('Creating new card:', newCard);
        const response = await axiosInstance.post('/cards', newCard);
        console.log('Create card response:', response);

        // Check for both possible response formats
        if (response.data.success || response.data.status === 'success') {
          const newCardData = response.data.data;
          setCards([...cards, newCardData]);
          setShowOtpModal(false);
          setOtp('');
          setCardTypeToAdd(null);
        } else {
          throw new Error(response.data.message || 'Failed to create card');
        }
      } catch (err: any) {
        console.error('Error creating card:', err);
        setError(err.response?.data?.message || err.message || 'Failed to create card');
      } finally {
        setIsLoading(false);
      }
    } else {
      alert('Invalid OTP. Please try again.');
      setOtp('');
    }
  };

  const handleViewCardDetails = async (cardId: number) => {
    if (!token) {
      setError('Authentication token not available');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Generate new RSA key pair
      const keyPair = generateKeyPair();
      dispatch(setKeys(keyPair));

      // Get encrypted card details from backend
      console.log('Fetching card details for card:', cardId);
      const response = await axiosInstance.post('/cards/' + cardId + '/security', { publicKey: keyPair.publicKey });
      console.log('Card details response:', response);

      // Check for both possible response formats
      if (!(response.data.success || response.data.status === 'success')) {
        throw new Error(response.data.message || 'Failed to fetch card details');
      }
      
      const data = response.data.data;
      
      // Decrypt the data
      const decryptedJson = decryptData(data.encryptedData, keyPair.privateKey);
      const cardDetails: CardDetails = JSON.parse(decryptedJson);

      // Update card in state with decrypted details
      setCards(cards.map(card => 
        card.id === cardId 
          ? { 
              ...card, 
              number: cardDetails.cardNumber,
              expiry: cardDetails.expiryDate,
              cvc: cardDetails.cvc 
            }
          : card
      ));

      // Show the sensitive data
      dispatch(setShowSensitiveData({ show: true, cardId }));

      // Set timeout to hide the data after 15 seconds
      const timeoutId = window.setTimeout(() => {
        dispatch(setShowSensitiveData({ show: false, cardId: null }));
        dispatch(clearCardSecurity());
      }, 15000);

      dispatch(setTimeoutId(timeoutId));

    } catch (err: any) {
      console.error('Error fetching card details:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    width: isMobile ? '100%' : 340,
    minWidth: isMobile ? 280 : 340,
    height: 200,
    borderRadius: 24,
    boxShadow: '0 8px 32px rgba(10,132,255,0.10)',
    color: '#fff',
    padding: 28,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginBottom: 32,
    position: 'relative',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  };

  const selectedCardData = (cards || []).find(card => card.id === selectedCard);

  // Update the card rendering to show/hide sensitive data
  const renderCardNumber = (card: any) => {
    if (cardSecurity.showSensitiveData && cardSecurity.cardId === card.id) {
      return card.number;
    }
    return card.number.replace(/\d(?=\d{4})/g, '•');
  };

  // Update the card click handler
  const handleCardClick = (card: any) => {
    if (selectedCard === card.id) {
      handleViewCardDetails(card.id);
    } else {
      setSelectedCard(card.id);
    }
  };

  const handleCardAction = async (cardId: string, action: 'freeze' | 'unfreeze' | 'cancel') => {
    try {
      console.log(`Performing ${action} on card:`, cardId);
      const response = await axiosInstance.patch('/cards/' + cardId + '/' + action);
      console.log(`${action} card response:`, response);
      
      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats
        if (data.success || data.status === 'success') {
          // Refresh cards list
          await fetchCards();
        } else {
          console.error(`Failed to ${action} card:`, data.message);
          setError(data.message || `Failed to ${action} card`);
        }
      }
    } catch (error: any) {
      console.error(`Error ${action} card:`, error);
      setError(error.response?.data?.message || error.message || `Error ${action} card`);
    }
  };

  const handleOrderCard = async (cardType: 'virtual' | 'physical') => {
    try {
      console.log('Ordering card:', cardType);
      const response = await axiosInstance.post('/cards/order', {
        type: cardType,
        deliveryAddress: cardType === 'physical' ? '123 Main St, City, Country' : null
      });
      console.log('Order card response:', response);
      
      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats
        if (data.success || data.status === 'success') {
          // Refresh cards list
          await fetchCards();
        } else {
          console.error('Failed to order card:', data.message);
          setError(data.message || 'Failed to order card');
        }
      }
    } catch (error: any) {
      console.error('Error ordering card:', error);
      setError(error.response?.data?.message || error.message || 'Error ordering card');
    }
  };

  const handleSecurityUpdate = async (cardId: string, securityData: any) => {
    try {
      console.log('Updating security for card:', cardId, securityData);
      const response = await axiosInstance.patch('/cards/' + cardId + '/security', securityData);
      console.log('Security update response:', response);
      
      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats
        if (data.success || data.status === 'success') {
          console.log('Security settings updated successfully');
        } else {
          console.error('Failed to update security settings:', data.message);
          setError(data.message || 'Failed to update security settings');
        }
      }
    } catch (error: any) {
      console.error('Error updating security settings:', error);
      setError(error.response?.data?.message || error.message || 'Error updating security settings');
    }
  };

  const handleLockCard = (cardId: number) => {
    setFrozenCards(prev => {
      const newFrozenCards = new Set(prev);
      if (newFrozenCards.has(cardId)) {
        newFrozenCards.delete(cardId);
      } else {
        newFrozenCards.add(cardId);
      }
      return newFrozenCards;
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: branding.pageBackground,
      fontFamily: branding.fontFamily,
      padding: isMobile ? '0' : 0,
      paddingBottom: isMobile ? '80px' : 0
    }}>
      {/* Loading State */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading cards...</div>
            <div style={{ color: '#666' }}>Please wait while we fetch your card information</div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          margin: '24px',
          padding: '16px',
          background: '#ffebee',
          border: '1px solid #f44336',
          borderRadius: 12,
          color: '#c62828',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button 
            onClick={() => {
              setError(null);
              fetchCards();
            }}
            style={{
              background: '#f44336',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={{
        maxWidth: branding.viewport.mainMaxWidth,
        margin: isMobile ? '0 auto' : '48px auto 0 auto',
        padding: isMobile ? '0 16px' : '0 24px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          marginBottom: 32,
          gap: isMobile ? 16 : 0,
        }}>
          <div>
            <h1 style={{
              color: branding.textColor,
              fontWeight: 700,
              fontSize: 28,
              margin: 0,
              marginBottom: 8,
            }}>Your Cards</h1>
            <p style={{
              color: '#666',
              fontSize: 16,
              margin: 0,
            }}>Manage your virtual and physical cards</p>
          </div>
          <div style={{ display: 'flex', gap: isMobile ? 12 : 16, width: isMobile ? '100%' : 'auto' }}>
            <button
              onClick={() => setShowAddCardModal(true)}
              style={{
                padding: '12px 24px',
                background: branding.primaryColor,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                flex: isMobile ? 1 : 'auto',
                justifyContent: 'center',
              }}
            >
              <span>➕</span> Add Card
            </button>
            <button
              onClick={() => setShowSecurityModal(true)}
              style={{
                padding: '12px 24px',
                background: '#fff',
                color: branding.textColor,
                border: '1.5px solid #eee',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: isMobile ? 1 : 'auto',
                justifyContent: 'center',
              }}
            >
              <span>🔒</span> Security
            </button>
          </div>
        </div>

        {/* Cards Section */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          marginBottom: 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <h3 style={{
            margin: 0,
            marginBottom: 24,
            color: branding.textColor,
            fontSize: 20,
            fontWeight: 600,
          }}>Your Cards</h3>
          
          {/* Empty State */}
          {!isLoading && cards.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              color: '#666'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>💳</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No Cards Found</div>
              <div style={{ marginBottom: 24 }}>You don't have any cards yet. Add your first card to get started.</div>
              <button
                onClick={() => setShowAddCardModal(true)}
                style={{
                  padding: '12px 24px',
                  background: branding.primaryColor,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Add Your First Card
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              {(cards || []).map(card => (
                <div
                  key={card.id}
                  style={{
                    ...cardStyle,
                    background: card.color,
                    transform: selectedCard === card.id ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: selectedCard === card.id ? '0 12px 40px rgba(10,132,255,0.15)' : '0 8px 32px rgba(10,132,255,0.10)',
                    position: 'relative',
                    filter: frozenCards.has(card.id) ? 'grayscale(0.8)' : 'none',
                    border: selectedCard === card.id ? '3px solid #0a84ff' : 'none',
                  }}
                  onClick={() => handleCardClick(card)}
                >
                  {/* Frozen overlay */}
                  {frozenCards.has(card.id) && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(128, 128, 128, 0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 16,
                      zIndex: 10,
                    }}>
                      <div style={{
                        color: '#fff',
                        fontSize: 18,
                        fontWeight: 700,
                        textAlign: 'center',
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                      }}>
                        Card Frozen
                      </div>
                    </div>
                  )}
                  {cardSecurity.showSensitiveData && cardSecurity.cardId === card.id && countdown !== null && (
                    <div style={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      background: 'rgba(0, 0, 0, 0.5)',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 'bold',
                      zIndex: 15,
                    }}>
                      {countdown}
                    </div>
                  )}
                  <div style={{ fontWeight: 600, fontSize: 18, opacity: 0.9 }}>{card.type} Card</div>
                  <div style={{ fontSize: 22, letterSpacing: 2, fontWeight: 700, margin: '16px 0 8px 0' }}>{renderCardNumber(card)}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, opacity: 0.8 }}>VALID THRU</div>
                      <div style={{ fontWeight: 600 }}>
                        {cardSecurity.showSensitiveData && cardSecurity.cardId === card.id ? card.expiry : '**/***'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <div style={{ fontSize: 13, opacity: 0.8 }}>CVC</div>
                      <div style={{ fontWeight: 600 }}>
                        {cardSecurity.showSensitiveData && cardSecurity.cardId === card.id ? card.cvc : '***'}
                      </div>
                    </div>
                    <img src={visaLogo} alt="VISA" style={{ height: 32, marginLeft: 16 }} />
                  </div>
                  <div style={{ position: 'absolute', bottom: 18, right: 28, fontSize: 13, opacity: 0.7 }}>{card.holder}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card Details and Analytics */}
        {selectedCard && selectedCardData && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 32,
            marginBottom: 32,
          }}>
            {/* Card Information */}
            <div style={{
              background: '#fff',
              borderRadius: 20,
              padding: 32,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <h3 style={{
                margin: 0,
                marginBottom: 24,
                color: branding.textColor,
                fontSize: 18,
                fontWeight: 600,
              }}>Card Information</h3>
              <div style={{ display: 'grid', gap: 16 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid #eee',
                }}>
                  <span style={{ color: '#666', fontSize: 14 }}>Card Type</span>
                  <span style={{ fontWeight: 600, color: branding.textColor }}>{selectedCardData.type}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid #eee',
                }}>
                  <span style={{ color: '#666', fontSize: 14 }}>Current Balance</span>
                  <span style={{ fontWeight: 600, color: branding.primaryColor }}>${selectedCardData.balance.toFixed(2)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid #eee',
                }}>
                  <span style={{ color: '#666', fontSize: 14 }}>Credit Limit</span>
                  <span style={{ fontWeight: 600, color: branding.textColor }}>${selectedCardData.limit.toFixed(2)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid #eee',
                }}>
                  <span style={{ color: '#666', fontSize: 14 }}>Available Credit</span>
                  <span style={{ fontWeight: 600, color: branding.successColor }}>
                    ${(selectedCardData.limit - selectedCardData.balance).toFixed(2)}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                }}>
                  <span style={{ color: '#666', fontSize: 14 }}>Status</span>
                  <span style={{
                    fontWeight: 600,
                    color: branding.successColor,
                    padding: '4px 12px',
                    background: '#e8f5e8',
                    borderRadius: 12,
                    fontSize: 12,
                  }}>{selectedCardData.status}</span>
                </div>
              </div>
            </div>

            {/* Spending Analytics */}
            <div style={{
              background: '#fff',
              borderRadius: 20,
              padding: 32,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <h3 style={{
                margin: 0,
                marginBottom: 24,
                color: branding.textColor,
                fontSize: 18,
                fontWeight: 600,
              }}>Spending by Category</h3>
              <div style={{ display: 'grid', gap: 16 }}>
                {(spendingCategories || []).map((cat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}>
                    <div style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: cat.color,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 4,
                      }}>
                        <span style={{
                          fontWeight: 500,
                          color: branding.textColor,
                        }}>{cat.category}</span>
                        <span style={{
                          fontWeight: 600,
                          color: branding.textColor,
                        }}>${cat.amount.toFixed(2)}</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: 4,
                        background: '#eee',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${cat.percentage}%`,
                          height: '100%',
                          background: cat.color,
                          borderRadius: 2,
                        }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recent Transactions */}
        {selectedCard && selectedCardData && (
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            marginBottom: 32,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: 24,
              color: branding.textColor,
              fontSize: 20,
              fontWeight: 600,
            }}>Recent Transactions</h3>
            <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
              <table style={{ width: '100%', minWidth: isMobile ? 500 : 'auto', borderCollapse: 'collapse', fontSize: 16 }}>
                <tbody>
                  {(selectedCardData?.transactions || []).map((tx: CardTransaction) => (
                    <tr key={tx.id} style={{
                      borderBottom: '1px solid #eee',
                      transition: 'background 0.2s',
                    }}>
                      <td style={{ padding: 12, display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                        <span style={{
                          background: '#f2f2f7', borderRadius: '50%', width: 36, height: 36,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12
                        }}>💳</span>
                        {tx.merchant}
                      </td>
                      <td style={{ padding: 12, color: branding.errorColor, fontWeight: 600, textAlign: 'right' }}>
                        -${Math.abs(tx.amount).toFixed(2)}
                      </td>
                      <td style={{ padding: 12, color: '#666', fontSize: 14, textAlign: 'right' }}>
                        {tx.date}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 500,
                          background: '#f0f0f0',
                          color: '#666',
                        }}>
                          {tx.category}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          marginBottom: 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <h3 style={{
            margin: 0,
            marginBottom: 24,
            color: branding.textColor,
            fontSize: 20,
            fontWeight: 600,
          }}>Quick Actions</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}>
            <button 
              onClick={() => selectedCard && handleLockCard(selectedCard)}
              disabled={!selectedCard}
              style={{
                padding: '16px 24px',
                background: selectedCard ? '#f7fafd' : '#f5f5f5',
                border: '1.5px solid #eee',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                color: selectedCard ? branding.textColor : '#999',
                cursor: selectedCard ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                transition: 'all 0.2s',
                opacity: selectedCard ? 1 : 0.6,
              }}
            >
              <span>{selectedCard && frozenCards.has(selectedCard) ? '🔓' : '🔒'}</span> 
              {selectedCard && frozenCards.has(selectedCard) ? 'Unfreeze Card' : 'Lock Card'}
            </button>
            <button style={{
              padding: '16px 24px',
              background: '#f7fafd',
              border: '1.5px solid #eee',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              color: branding.textColor,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              transition: 'all 0.2s',
            }}>
              <span>📱</span> Add to Wallet
            </button>
            <button style={{
              padding: '16px 24px',
              background: '#f7fafd',
              border: '1.5px solid #eee',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              color: branding.textColor,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              transition: 'all 0.2s',
            }}>
              <span>📊</span> Spending Report
            </button>
            <button style={{
              padding: '16px 24px',
              background: '#f7fafd',
              border: '1.5px solid #eee',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              color: branding.textColor,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              transition: 'all 0.2s',
            }}>
              <span>⚙️</span> Card Settings
            </button>
          </div>
        </div>
      </div>

      {/* Add Card Modal */}
      {showAddCardModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 24, padding: isMobile ? 24 : 40,
            maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}>
            <h3 style={{
              margin: 0, marginBottom: 24, color: branding.textColor,
              fontSize: isMobile ? 20 : 24, fontWeight: 700,
            }}>Choose Card Type</h3>
            <p style={{ margin: '0 auto 32px auto', color: '#666', maxWidth: 320 }}>
              Select whether you want to create a new virtual or physical card.
            </p>
            <div style={{ display: 'grid', gap: 16 }}>
              <button
                onClick={() => handleCardTypeSelect('Virtual')}
                style={{
                  padding: '24px', background: '#f7fafd', border: '1.5px solid #eee',
                  borderRadius: 16, fontSize: 18, fontWeight: 600, color: branding.textColor,
                  cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16,
                }}
              >
                <span>📱</span>
                <div>
                  <div>Virtual Card</div>
                  <div style={{ fontSize: 14, fontWeight: 400, color: '#666' }}>Instantly issued for online use.</div>
                </div>
              </button>
              <button
                onClick={() => handleCardTypeSelect('Physical')}
                style={{
                  padding: '24px', background: '#f7fafd', border: '1.5px solid #eee',
                  borderRadius: 16, fontSize: 18, fontWeight: 600, color: branding.textColor,
                  cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16,
                }}
              >
                <span>💳</span>
                <div>
                  <div>Physical Card</div>
                  <div style={{ fontSize: 14, fontWeight: 400, color: '#666' }}>Delivered to your address.</div>
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowAddCardModal(false)}
              style={{
                marginTop: 24, background: 'transparent', border: 'none',
                color: '#666', cursor: 'pointer', fontSize: 16,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1010, padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 24, padding: isMobile ? 24 : 40,
            maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}>
            <h3 style={{
              margin: 0, marginBottom: 16, color: branding.textColor,
              fontSize: isMobile ? 20 : 24, fontWeight: 700,
            }}>Enter OTP</h3>
            <p style={{ margin: '0 auto 32px auto', color: '#666', maxWidth: 320 }}>
              An authorization code was sent to your phone. (Hint: it's 123456)
            </p>
            <input
              type="tel"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
              maxLength={6}
              style={{
                width: '100%',
                padding: '16px 0',
                fontSize: 32,
                fontWeight: 700,
                textAlign: 'center',
                letterSpacing: isMobile ? 8 : 16,
                border: '2px solid #eee',
                borderRadius: 12,
                marginBottom: 32,
                color: branding.textColor,
              }}
            />
            <button
              onClick={handleVerifyOtp}
              style={{
                width: '100%', padding: '16px 0', background: branding.primaryColor,
                border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600,
                color: '#fff', cursor: 'pointer',
              }}
            >
              Verify & Add Card
            </button>
             <button
              onClick={() => setShowOtpModal(false)}
              style={{
                marginTop: 16, background: 'transparent', border: 'none',
                color: '#666', cursor: 'pointer', fontSize: 16,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Security Modal */}
      {showSecurityModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: 40,
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: 24,
              color: branding.textColor,
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
            }}>Card Security Settings</h3>
            
            <div style={{ display: 'grid', gap: 16, marginBottom: 32 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                background: '#f7fafd',
                borderRadius: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: branding.textColor }}>Online Transactions</div>
                  <div style={{ fontSize: 14, color: '#666' }}>Allow online purchases</div>
                </div>
                <label style={{
                  width: 48,
                  height: 24,
                  background: branding.primaryColor,
                  borderRadius: 12,
                  position: 'relative',
                  cursor: 'pointer',
                }}>
                  <input type="checkbox" style={{ opacity: 0, width: 0, height: 0 }} defaultChecked />
                  <span style={{
                    width: 20,
                    height: 20,
                    background: '#fff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    transition: 'all 0.2s',
                  }} />
                </label>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                background: '#f7fafd',
                borderRadius: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: branding.textColor }}>International Transactions</div>
                  <div style={{ fontSize: 14, color: '#666' }}>Allow foreign purchases</div>
                </div>
                <label style={{
                  width: 48,
                  height: 24,
                  background: '#ccc',
                  borderRadius: 12,
                  position: 'relative',
                  cursor: 'pointer',
                }}>
                  <input type="checkbox" style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{
                    width: 20,
                    height: 20,
                    background: '#fff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    transition: 'all 0.2s',
                  }} />
                </label>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                background: '#f7fafd',
                borderRadius: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: branding.textColor }}>Transaction Alerts</div>
                  <div style={{ fontSize: 14, color: '#666' }}>Get notified of all transactions</div>
                </div>
                <label style={{
                  width: 48,
                  height: 24,
                  background: branding.primaryColor,
                  borderRadius: 12,
                  position: 'relative',
                  cursor: 'pointer',
                }}>
                  <input type="checkbox" style={{ opacity: 0, width: 0, height: 0 }} defaultChecked />
                  <span style={{
                    width: 20,
                    height: 20,
                    background: '#fff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    transition: 'all 0.2s',
                  }} />
                </label>
              </div>
            </div>

            <button
              onClick={() => setShowSecurityModal(false)}
              style={{
                width: '100%',
                padding: '16px 0',
                background: branding.primaryColor,
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
              }}
            >
              Save Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cards; 